import { useState } from "react";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { useQuery } from "@tanstack/react-query";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { getDelyvaCreateOrderLog } from "services/data.internal.service";
import { LoadingDialog } from "ui-components/Alert";
import { FormTitle } from "ui-components/Form";
import { Texts } from "ui-components/Form";
import { Layout } from "ui-components/Layout";
import BasicTable from "ui-components/tables/BasicTable";

// Delyva stores Request/Response as raw JSON strings - re-indent them for readability.
// Falls back to the raw string if it isn't valid JSON so nothing gets swallowed silently.
const prettyJson = (str) => {
  if (!str) return "";
  try {
    return JSON.stringify(JSON.parse(str), null, 2);
  } catch {
    return str;
  }
};

const jsonBoxSx = {
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  backgroundColor: ({ palette }) => (palette.mode === "dark" ? palette.background.card : palette.grey[100]),
  p: 2,
  borderRadius: "0.5rem",
  fontSize: "0.8rem",
  maxHeight: "18rem",
  overflow: "auto",
};

function DelyvaCreateOrderLog() {
  const [custOrdNbr, setCustOrdNbr] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["delyva-create-order-log", searchValue],
    queryFn: () => getDelyvaCreateOrderLog({ custOrdNbr: searchValue }),
    enabled: Boolean(searchValue),
  });

  const logs = data?.data?.data ?? [];

  const onSearch = () => {
    const value = custOrdNbr.trim();
    if (value === searchValue) refetch();
    else setSearchValue(value);
  };

  const columns = [
    { accessorKey: "docNo1", header: "Order Number", align: "left" },
    { accessorKey: "docNo2", header: "Cust Order Number", align: "left" },
    { accessorKey: "createdOn", header: "Created On", align: "left" },
    {
      accessorKey: "view",
      header: "",
      align: "center",
      cell: ({ row }) => (
        <MDButton size="small" color="info" onClick={() => setSelectedLog(row.original)}>
          View
        </MDButton>
      ),
    },
  ];

  return (
    <Layout>
      {isFetching && <LoadingDialog />}
      <FormTitle title="Delyva Create Order Log" />
      <Grid sx={{ padding: "10px 0 0 10px" }} container spacing={3} alignItems="flex-end">
        <Grid item xs={8}>
          <Texts
            field={{ name: "custOrdNbr", value: custOrdNbr, label: "Cust Order Number" }}
            onChange={(e) => setCustOrdNbr(e.target.value)}
          />
        </Grid>
        <Grid item xs={4}>
          <MDButton
            variant="gradient"
            color="info"
            onClick={onSearch}
            disabled={!custOrdNbr.trim()}
          >
            Search
          </MDButton>
        </Grid>
      </Grid>

      <MDBox mt={3}>
        <BasicTable columns={columns} data={logs} />
      </MDBox>

      <Dialog open={Boolean(selectedLog)} onClose={() => setSelectedLog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Create Order Log Detail</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="button" fontWeight="bold">
            Request
          </MDTypography>
          <MDBox component="pre" sx={jsonBoxSx}>
            {prettyJson(selectedLog?.request)}
          </MDBox>

          <MDTypography variant="button" fontWeight="bold" mt={2} display="block">
            Response
          </MDTypography>
          <MDBox component="pre" sx={jsonBoxSx}>
            {prettyJson(selectedLog?.response)}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" onClick={() => setSelectedLog(null)}>
            Close
          </MDButton>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

export default DelyvaCreateOrderLog;
